import type { GalleryMedia } from '@travel/ui/components/Gallery';

import type { TimeZoneData } from './providerInformation';
import type { SalesPromotion as CommonSalesPromotion } from './providerList';

import type { ProviderContractType } from '../constants';
import type {
  PLAN_PAYMENT_MANAGEMENT_NAME,
  SP_PAYMENT_MANAGEMENT_NAME,
} from '../constants/provider';

export type ProviderRatePlan = { items: RatePlanItems; filters: Array<RatePlanFilter> };
export type RatePlanItems = Array<RatePlanItem>;

export type FilterFeatureItem = {
  id: string;
  text: string;
  icon?: string;
  isSelected?: boolean;
};

export type RatePlanFilter = {
  groupId: string;
  groupName: string;
  groupOrder: number;
  features?: Array<RatePlanFilterFeature>;
  // price
  min?: number;
  max?: number;
  freeCancellation?: FreeCancellation;
};

type FreeCancellation = {
  selected: boolean;
  hits?: number;
  name: string;
};

export type RatePlanFilterFeature = {
  featureId: string;
  featureOrder?: number;
  name?: string;
  selected?: boolean;
  managementName?: null | string;
};

export type SalesPromotionPaymentRestrictionFeature = {
  text: string;
  managementName: SP_PAYMENT_MANAGEMENT_NAME;
};

export type PlanPaymentRestrictionFeature = {
  text: string;
  managementName: PLAN_PAYMENT_MANAGEMENT_NAME;
};

export type SalesPromotion = {
  features?: Array<SalesPromotionFeature>;
} & CommonSalesPromotion;

export type Feature = {
  id: string;
  text: string;
  icon?: string;
  title?: string;
  description?: string;
};

export type CombinedIncentive = {
  totalPointIncentive: number;
  totalDiscountIncentive: number;
};

export type Price = {
  /** Number of price before discounted */
  base: number;
  /** Total Price */
  total: number;
  salesPromotions: SalesPromotion[];
  /** Number of price discountRate */
  discountRate?: number;
  /** unit rate of the item */
  unitRate: number;
  /** flag for the price inclusive tax */
  isConsumptionTaxIncluded?: boolean;
  /** discounted unit rate in case of promotions */
  discountedUnitRate?: number;
  combinedIncentive?: CombinedIncentive;
};
type SalesPromotionFeature = {
  id: string;
  name: string;
};

export type Facility = {
  group: string;
  features: Array<Feature>;
};

export type View = {
  id: string;
  text: string;
  title?: string;
  description?: string;
  icon?: string;
};

export type CancellationPenalties = {
  deadline?: string;
  deadlineDays?: number;
  deadlineTime?: string;
  percentPenalty?: number;
  type: string;
  timeZoneData?: TimeZoneData;
};

export type Plan = {
  nonRefundable: boolean;
  itemRatePlanId: string; // item plan link id
  planId: string; // plan id
  name: string;
  description?: string;
  isDescriptionGoogleTranslated?: boolean;
  cancellationPenalties?: Array<CancellationPenalties>;
  facilities: Array<Facility>;
  hasMeal: boolean;
  media: Array<GalleryMedia>;
  price: Price;
  taxesAndFees: {
    included: Array<TaxAndFee>;
    excluded: Array<TaxAndFee>;
  };
  points?: {
    amount: number;
    currency: string;
  };
  paymentMethods: Array<string>;
  noOfPlanLeft?: number;
  paymentRestrictionFeature?: PlanPaymentRestrictionFeature;
  availabilityCount?: number;
  cancellationRemarks?: string;
  hasMemberOnly?: boolean;
};

export enum TAXES_AND_FEES_LABEL {
  CITY_TAX = 'Traveler_Enumeration.Tax_Fee_Code.City_Tax',
  TOTAL_TAX = 'Traveler_Enumeration.Tax_Fee_Code.Total_Tax',
  VALUE_ADDED_TAX = 'Traveler_Enumeration.Tax_Fee_Code.Value_Added_Tax',
  CONSUMPTION_TAX = 'Traveler_Enumeration.Tax_Fee_Code.Consumption_Tax',
  HOTEL_TAX = 'Traveler_Enumeration.Tax_Fee_Code.Hotel_Tax',
  HOT_SPRING_TAX = 'Traveler_Enumeration.Tax_Fee_Code.Hot_Spring_Tax',
  CLEANING_FEE = 'Traveler_Enumeration.Tax_Fee_Code.Cleaning_Fee',
  SERVICE_CHARGE_FEE = 'Traveler_Enumeration.Tax_Fee_Code.Service_Charge_Fee',
  RESORT_FEE = 'Traveler_Enumeration.Tax_Fee_Code.Resort_Fee',
}

export type TaxesAndFeesCode =
  | 'CITY_TAX'
  | 'TOTAL_TAX'
  | 'VALUE_ADDED_TAX'
  | 'CONSUMPTION_TAX'
  | 'HOTEL_TAX'
  | 'HOT_SPRING_TAX'
  | 'CLEANING_FEE'
  | 'SERVICE_CHARGE_FEE'
  | 'RESORT_FEE';

export type TaxAndFee = {
  ageFrom?: number;
  ageTo?: number;
  amount?: number;
  appliedFrom?: string;
  appliedTo?: string;
  chargeUnit?: string;
  code: TaxesAndFeesCode;
  currency?: string;
  name: string;
  percentage?: number;
};

export type Capacity = {
  minGuests?: number;
  maxGuests?: number;
  maxAdults?: number;
  maxChildren?: number;
};

export type BedSizeItem = {
  name: string;
  amount: number;
  logicGroupNumber?: number;
};

export type RoomSizeItem = {
  area: number;
  unit: string;
};

export type RatePlanItem = {
  id: string;
  name: string;
  description?: string;
  bedSizes: Array<BedSizeItem>;
  roomSize?: RoomSizeItem;
  capacity: Capacity;
  view: View;
  smokingAllowed?: boolean;
  media: Array<GalleryMedia>;
  facilities: Array<Facility>;
  plans: Array<Plan>;
  isGoogleTranslated: boolean;
  price?: Price;
  contractType?: ProviderContractType;
};

export type ProviderRatePlanErrors = {
  statusCode: number;
  message: string;
};

export type CouponData = {
  salesPromotionId: string;
  itemId: string;
  ratePlanId: string;
  planId: string;
};

export type RoomDetail = {
  facilities: Array<Facility>;
  views: View;
  lowestPlanPrice: number;
  displayImage: GalleryMedia;
  media: Array<GalleryMedia>;
  plans: Array<Plan>;
  name: string;
  description?: string;
  smokingAllowed?: boolean | null;
  capacityText: string | [];
  bedText: string;
  roomText?: string | null;
  isGoogleTranslated: boolean;
};
